import { createSelector } from '@reduxjs/toolkit';

import { authSelectors } from 'app-components/flux/auth';
import { selectorsHelpers } from 'app-components/flux/core';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { preferencesSelectors } from 'app-components/flux/preferences';
import { userInfoSelectors } from 'app-components/flux/user-info';
import { isDemoMode, localPreferences } from 'app-components/utils/core';

import { businessSelectors } from 'app-components/flux/business';
import { getTopTierEntitlement } from 'app-components/flux/entitlements/entitlementsSelectors';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { getPreferenceValue } from 'app-components/flux/preferences-v2/preferencesV2Selectors';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';

const clientType = 'Simplifi Web App';

// Selector to return list of all column names visible in transaction register
const getTransactionRegisterVisibleColumnsList = createSelector(
  (state) => preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] }), // Preference selector for old transaction register
  (state) => getPreferenceValue(state, 'sTransactionGrid'), // Preference selector for new transaction register
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'newDatagridRegister'),
  (datasetPreferences, gridDisplayOptions, newTxnRegister) =>
    newTxnRegister
      ? Object.keys(gridDisplayOptions?.columnVisibilityModel ?? {}).filter(
          (columnName) => gridDisplayOptions.columnVisibilityModel[columnName] && columnName !== 'threeDotMenu',
        )
      : [...(datasetPreferences?.transactionRegister?.regFieldsSort?.cashFlow ?? [])].filter(
          (columnName) => columnName !== 'select' && columnName !== 'menu',
        ),
);

export const getMixpanelUserData = createSelector(
  authSelectors.getAuthSession,
  subscriptionsSelectors.getActiveSubscription,
  userInfoSelectors.getUserInfo,
  businessSelectors.getMixpanelBusinessData,
  getTopTierEntitlement,
  getTransactionRegisterVisibleColumnsList,
  selectorsHelpers.createValueCache(
    (authSession, subscription, userInfo, businessData, datasetEntitlement, visibleColumnList) => ({
      qcs_id: authSession.qcsId ?? 'unknown',
      ...(authSession.datasetId && { dataset_id: authSession.datasetId }),
      ...(authSession.scope && { scope: authSession.scope }),
      ...(authSession.groups && { groups: authSession.groups }),
      ...(userInfo?.source && { source: userInfo.source }),
      ...(subscription?.active !== undefined && { subscriber: subscription.active ? 'Subscribed' : 'Not Subscribed' }),
      ...(subscription?.productLineName && { product_line: subscription.productLineName }),
      ...(subscription?.tierName && { tier: subscription.tierName }),
      ...(subscription?.tierUriName && { subscription_tier: subscription.tierUriName }),
      ...(datasetEntitlement?.id && { dataset_tier: datasetEntitlement.id }),
      ...(subscription?.isInTrialPeriod != null && {
        subscription_state: subscription.isInTrialPeriod ? 'trial' : 'paid',
      }),
      ...(subscription?.renewalFrequency != null && {
        subscription_term: subscription.renewalFrequency === 'P1M' ? 'monthly' : 'annual',
      }),
      ...(businessData && businessData),
      current_txn_columns: visibleColumnList ?? [],
      last_client: clientType,
      simplifi_web_app_version: process.env.APP_VERSION,
    }),
  ),
);

export const getMixpanelDefaultProperties = createSelector(
  authSelectors.getAuthSession,
  entitlementsSelectors.getEntitlements,
  userInfoSelectors.getUserInfo,
  preferencesSelectors.getWebThemes,
  selectorsHelpers.createValueCache((authSession, entitlements, userInfo, themes) => {
    const defaultProps = {};

    defaultProps.qcs_id = authSession?.qcsId ?? 'unknown';
    defaultProps.dataset_id = authSession?.datasetId;
    defaultProps.dataset_platform = authSession?.datasetPlatform;
    defaultProps.dataset_created_by = authSession?.datasetCreatedByClientId;
    defaultProps.scope = authSession?.scope;
    defaultProps.groups = authSession?.groups;
    defaultProps.source = userInfo?.source;
    defaultProps.current_appearance = themes.current;
    defaultProps.system_appearance = themes.system;
    defaultProps.early_access = Boolean(authSession.groups?.includes('qcs_acme_beta'));

    defaultProps.entitlements =
      entitlements &&
      entitlements
        .filter((entitlement) => entitlement.active)
        .map((entitlement) => entitlement.id)
        .toIndexedSeq()
        .toArray();

    defaultProps.persist = localPreferences.getPersist();
    defaultProps.keep_logged_in = localPreferences.getKeepLoggedIn();
    defaultProps.demo_mode = isDemoMode();

    return defaultProps;
  }),
);
